import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { ToastContainer, toast } from "react-toastify";
import * as helpers from "../../../helpers/helpers";
import http from "../../../helpers/http";
import LoadingScreen from "../../common/LoadingScreen";
import MultiRangeSlider from "../multirangeslider/MultiRangeSlider";
import SearchResultsComponent from "./Result-search";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { setSearchQuery } from "../../../states/actions/searchQueryActions";
import { FETCH_PROPERTIES_CONTENT_FAILED } from "../../../states/actions/actionTypes";
import { toggleResultView } from "../../../states/actions/resultView";
import usePreviousURLParams from "./prevurl";
import NewFiltersForm from "./new-filters-form";
import httpos from "../../../helpers/httpos";
import PaginatedWrapper from "./paginate-wrapper";
import { updatePropertyTableColumns } from "../../../states/actions/fetchSiteSettings";
import {
  normalizeSearchPage,
  SEARCH_RESULT_LIMIT,
  SEARCH_RESULTS_PER_PAGE,
} from "./searchResultDisplay";
import useHeightCssVar from "../../../hooks/useHeightCssVar";

const FiltersSearchNew = (props) => {
  const searchInputRef = useRef(null);
  // The fixed filter bar wraps onto two lines between the ipad breakpoint and
  // ~1220px (iPad Pro 13" portrait is 1032px); the results toolbar, list and
  // map are offset by this measured height instead of assuming one line.
  const filterBarRef = useRef(null);
  useHeightCssVar(filterBarRef, "--search-filter-bar-height");
  const [searchParams] = useSearchParams();

  const [search_p, setSearchP] = useState("");
  const [searchQueryValues, setSearchQueryValues] = useState(null);
  const dispatch = useDispatch();

  const location = useLocation();
  const { pathname } = location;
  const segments = pathname.split("/");

  const queryParams = new URLSearchParams(location.search);
  // Legacy params (no longer used) that can persist by being carried forward
  queryParams.delete("popular_property_type");
  queryParams.delete("more_property_type");

  const propertyTypeExists = searchQueryValues?.hasOwnProperty("property_type");
  const [sortValue, setSortValue] = useState("property_sf");

  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [nextSearchAfter, setnextSearchAfter] = useState(null);

  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResultsData, setSearchResultsData] = useState(null);
  const [activeSearchRequest, setActiveSearchRequest] = useState(null);
  const [totalSearchRecords, setTotalPropertiesRecords] = useState(null);

  const [isLoadingform, setIsLoadingform] = useState(false);

  const searchButtonRef = useRef(null);

  const {
    ToggleHideSearch,
    hideSearch,
    Maptoggle,
    showMap,
    ownertoggle,
    setShowMap,
    search_keyword,
    searchRef,
  } = props;
  const property_table_columns = useSelector(
        (state) => state.fetchSiteSettings.property_table_columns
      );
  const navigate = useNavigate();
  const searchApiRequest = (searchFormData, onLoad = false, pageNo = null,removeGeometry=false,sortBy=null) => {
    if (!searchFormData?.geometry && removeGeometry=== false) {
      const geometry_polygon = helpers.getUrlParameter("geometry");
      let geometry = props.drawnFeatures || geometry_polygon || null;
      searchFormData = { ...searchFormData, geometry: geometry };
    }
    if (parseInt(pageNo) > 0) {
      const safePageNo = normalizeSearchPage(
        pageNo,
        SEARCH_RESULT_LIMIT,
        SEARCH_RESULTS_PER_PAGE
      );
      searchFormData = { ...searchFormData, page: safePageNo,search_after:nextSearchAfter };
    }
    // PROPERTY-TYPE-EMPTY-FIX (2/3): strip primitives that are empty AND array
    // fields whose elements are all empty (e.g. property_type: [""] coming from
    // a stale URL parse). Without this, the Apply path POSTs property_type[]=""
    // and the backend returns "properties not found".
    const isEmptyValue = (v) =>
      v === null || v === undefined || v === '' || v === false ||
      v === 'null' || v === 'undefined' || v === 'false';
    searchFormData = Object.fromEntries(
      Object.entries(searchFormData)
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            return [key, value.filter((v) => !isEmptyValue(v))];
          }
          return [key, value];
        })
        .filter(([_, value]) => {
          if (Array.isArray(value)) return value.length > 0;
          return !isEmptyValue(value);
        })
    );
    // DEEP-LINK-GUARD: On initial onLoad, defensively re-read URL params from
    // window.location.search (always current — bypasses any stale closure of
    // useLocation()) and fill in any keys missing from searchFormData. This
    // fixes the deep-link race where the first /search-data request sometimes
    // omits filter params (e.g. building_roofType) and returns unfiltered
    // results. Only fills MISSING keys — never overrides values the caller set.
    // TO REVERT: remove this `if (onLoad) { ... }` block.
    if (onLoad) {
      const freshUrlParams = new URLSearchParams(window.location.search);
      freshUrlParams.delete("popular_property_type");
      freshUrlParams.delete("more_property_type");
      for (const [key, value] of freshUrlParams.entries()) {
        if (value && value !== "null" && value !== "undefined" && !(key in searchFormData)) {
          searchFormData[key] = value;
        }
      }
    }
    if (parseInt(searchFormData.page) > 0) {
      searchFormData = {
        ...searchFormData,
        page: normalizeSearchPage(
          searchFormData.page,
          SEARCH_RESULT_LIMIT,
          SEARCH_RESULTS_PER_PAGE
        ),
      };
    }
    // PHP-COMPAT-FIX: Backend (PHP) expects comma-separated strings, not
    // key[]=val arrays. doObjToFormData defaults to key[] for JS arrays, which
    // makes $_POST['field'] a PHP array — the backend then casts it to string
    // when building the OpenSearch query and triggers a "Array to string
    // conversion" warning, returning {status:false, properties:[]}.
    // Refresh/URL path already sends as comma-strings (encodeURIComponent on a
    // JS array coerces to "val1,val2"); this normalizes the Apply path to match.
    // TO REVERT: remove this Object.fromEntries(...) block.
    searchFormData = Object.fromEntries(
      Object.entries(searchFormData).map(([key, value]) =>
        Array.isArray(value) ? [key, value.join(",")] : [key, value]
      )
    );
    searchFormData={...searchFormData, host: window.location.hostname, sort: sortValue };
    if(onLoad){
      searchFormData = { ...searchFormData, sort: sortBy || sortValue };
    }
    if(removeGeometry){
      localStorage.removeItem("drawnFeatures");
      delete searchFormData.geometry;
      const url = new URL(window.location.href);
      url.searchParams.delete("geometry");
      window.location.href = url.toString();
    // window.history.replaceState(null, "", url.toString());
    
    }
    setIsLoadingform(true);
    searchFormData = { ...searchFormData, property_table_columns: JSON.stringify(property_table_columns) };
    httpos
      .post("/search-data", helpers.doObjToFormData(searchFormData))
      .then(({ data }) => {
        console.log(data, "search data");
        if(data?.property_table_columns){
          dispatch(updatePropertyTableColumns(data?.property_table_columns));
        }
        setnextSearchAfter(data?.nextSearchAfter);
        renderMap(data.parcel_ids, searchFormData);
        const properties_list = JSON.stringify(data);
        localStorage.setItem("listingData", properties_list);
        localStorage.setItem("currentFilters", JSON.stringify(searchFormData));
        setActiveSearchRequest(searchFormData);
        setSearchResultsData([]);
        setSearchResultsData(JSON.stringify(data));
        setTotalPropertiesRecords(data.totalRecords ? Number(data.totalRecords) : 0);
        
    setSearchAfterMap(prev => ({
  ...prev,
  ...data?.nextSearchAfter
}));
        setShowSearchResults(true);
        ownertoggle(true);
        setIsLoadingform(false);
      })
      .catch((error) => {
        setIsLoadingform(false);
        console.log(error);
      });
  };
  const [currentPageGroup, setCurrentPageGroup] = useState(1); // group 1 = pages 1-200
const [searchAfterMap, setSearchAfterMap] = useState({});
const startPage = (currentPageGroup - 1) * 200 + 1;
const endPage = currentPageGroup * 200;
const loadNextPageGroup = () => {
  const nextGroup = currentPageGroup + 1;
  const basePage = (nextGroup - 1) * 200 + 1;

  if (!searchAfterMap[basePage - 1]) {
    console.error("Need search_after for new group");
    return;
  }

  // optionally preload pages 201–400
  setCurrentPageGroup(nextGroup);
};

const loadPreviousPageGroup = () => {
  setCurrentPageGroup(currentPageGroup - 1);
};

  const handlePageClick = (event) => {
    const absolutePage = startPage + event.selected;
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("popular_property_type");
    urlParams.delete("more_property_type");
    

    const geometry = urlParams.get("geometry");
    if (document.getElementById("outer_properties")) {
      window.scroll({
        top: document.getElementById("outer_properties").offsetTop,
        left: 0,
        behavior: "smooth",
      });
    }

   setPage(absolutePage);
    urlParams.set("page", absolutePage);
    urlParams.set("geometry", geometry);
    const all_url_params = {};
    for (const [key, value] of urlParams.entries()) {
      all_url_params[key] = value;
    }
    if (absolutePage <= 200) {
      // window.location.href = `/search-property?${all_url_params.toString()}`;
      navigate(
        `/search-property?${urlParams.toString()}`
      );
      searchApiRequest(all_url_params, false, absolutePage);
    } else {
      const prevSort = searchAfterMap[absolutePage - 1];// sort value from previous page
      if (!prevSort) {
      console.error("No search_after value for this page");
      return;
    }
    searchApiRequest(all_url_params, prevSort, absolutePage);
    }
  };

  const handleSubmit = (event,sortBy) => {
    event.preventDefault();
    const allParams = {};
      // Always re-read from current URL, and drop legacy params.
      const freshParams = new URLSearchParams(location.search);
      freshParams.delete("popular_property_type");
      freshParams.delete("more_property_type");
      for (const [key, value] of freshParams.entries()) {
        allParams[key] = value;
      }
      allParams["host"] = window.location.hostname;
      allParams["sort"] = sortBy || sortValue;
      allParams["geometry"] = allParams?.geometry || props.drawnFeatures || null;
      allParams["page"] = 1;
      
      const searchParams = new URLSearchParams(allParams);
      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      navigate(
      `/search-property?${searchParams.toString()}`
    );
     searchApiRequest(allParams, false, page);
      // window.location.href = newUrl;
    
  };
  const renderMap = (parcel_ids, filtervalues) => {};
  
  return (
        <>
            {isLoadingform && <LoadingScreen />}
            <ToastContainer />
            <div className="new_outer_filter_new" ref={filterBarRef}>
                <div className="contain-fluid">
                    <div className="mob_filter_view">
                        {/* Breadcrumbs */}
                        <div className="colL">
                            <span>Home</span> <span>{">"}</span> <span>Search</span>
                        </div>
                    </div>

                    {/* Search and Filters Form Component */}
                    <NewFiltersForm
                        setIsLoadingform={setIsLoadingform}
                        setSearchResultsData={setSearchResultsData}
                        setTotalPropertiesRecords={setTotalPropertiesRecords}
                        setShowSearchResults={setShowSearchResults}
                        renderMap={renderMap}
                        ownertoggle={ownertoggle}
                        hideSearch={hideSearch}
                        ToggleHideSearch={ToggleHideSearch}
                        showMap={showMap}
                        drawnFeatures={props.drawnFeatures}
                        setDrawnFeatures={props.setDrawnFeatures}
                        setSortValue={setSortValue}
                        sortValue={sortValue}
                        searchApiRequest={searchApiRequest}
                    />
                </div>
            </div>

            {/* Conditionally render search results */}
            {showSearchResults && (
                <SearchResultsComponent
                    data={searchResultsData}
                    hideSearch={hideSearch}
                    ToggleHideSearch={ToggleHideSearch}
                    sortValue={sortValue}
                    setSortValue={setSortValue}
                    handleSubmit={handleSubmit}
                    propertyTypeExists={propertyTypeExists}
                    searchedPage={page}
                    drawnFeatures={props.drawnFeatures}
                    setDrawnFeatures={props.setDrawnFeatures}
                    Maptoggle={Maptoggle}
                    setSearchQueryValues={setSearchQueryValues}
                    activeSearchRequest={activeSearchRequest}
                />
            )}

            {/* Conditionally render pagination */}
            {searchResultsData?.length > 0 && (
                <div className="pagination_container">
                    <PaginatedWrapper
                        totalRecords={totalSearchRecords}
                        apiFunction={searchApiRequest}
                        initialPage={1}
                        nextSearchAfter={nextSearchAfter}
                        sortValue={sortValue}
                    />
                </div>
            )}
        </>
  );
};

export default FiltersSearchNew;
